import { useEffect } from "react";
import { useRouter } from "next/router";
import { format, subDays } from "date-fns";
import { useUserContext } from "../../../contexts/UserProvider";
import useUpdateEffect from "../../../hooks/useUpdateEffect";

/**
 * Forces the default dashboard filter query params for normal & MoneyHash users:
 * - MoneyHash users without an `organization` get their own `organizationId`.
 * - Missing `start_date` defaults to 30 days ago; missing `end_date` defaults to today.
 * - Missing `account` defaults to an empty string (normal users).
 * - Missing `provider_live_status` defaults to `"true"`.
 * - When a MoneyHash user has an `organization`, the `account` param is dropped.
 */
export function useDefaultDashboardFilters() {
  const router = useRouter();
  const { start_date, end_date, account, organization, provider_live_status } =
    router.query;
  const { isMoneyHashUser, organizationId } = useUserContext();

  useEffect(() => {
    if (
      (!organization && isMoneyHashUser) ||
      !start_date ||
      !end_date ||
      (!account && account !== "") ||
      !provider_live_status
    ) {
      router.replace({
        query: {
          ...router.query,
          ...(isMoneyHashUser &&
            !organization && { organization: organizationId }),
          ...(!start_date && {
            start_date: format(subDays(new Date(), 30), "yyyy-MM-dd"),
          }),
          ...(!end_date && { end_date: format(new Date(), "yyyy-MM-dd") }),
          ...(!account && { account: "" }),
          ...(!provider_live_status && { provider_live_status: "true" }),
        },
      });
    }
  }, [
    organizationId,
    isMoneyHashUser,
    organization,
    start_date,
    end_date,
    account,
    provider_live_status,
    router,
  ]);

  useUpdateEffect(
    () => {
      if (isMoneyHashUser && organization) {
        const { account: _account, ...rest_params } = router.query;
        router.replace({
          query: {
            ...rest_params,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organization, isMoneyHashUser]
  );
}
